import { encodeAbiParameters, keccak256 } from 'viem'
import {
  liquityBranchesByChain,
  liquityConfigFor,
  LiquityBranchConfig,
} from '@1delta/data-sdk'
import { LiquityTroveManagerAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { Call } from '../../../utils/multicall'
import {
  decodeLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

/**
 * Trove discovery + user-data call build for the Liquity family.
 *
 * TroveNFT is NOT enumerable, so a user's troves are discovered by
 * DETERMINISTIC ID PROBING: `troveId = keccak256(abi.encode(sender, owner,
 * ownerIndex))`, where the official frontends assign ownerIndex sequentially
 * from 0. We probe:
 *  - direct opens: sender = owner = user, ownerIndex 0..K-1;
 *  - zapper opens (the common case — official frontends route through
 *    zappers): sender = each configured zapper, with the zapper's transformed
 *    index `uint256(keccak256(abi.encode(user, ownerIndex)))`.
 *
 * One status multicall over all candidates per branch classifies them
 * (status 1 = active, 4 = zombie). NOT covered: troves RECEIVED by NFT
 * transfer and exotic owner ≠ sender opens — those need the subgraph
 * enrichment path (planned) or an explicit trove-id override.
 *
 * The discovered per-branch layout is stashed in a short-lived module cache
 * so the parser (a separate phase fed only the multicall results) can slice
 * them — same trick as the Lista broker caches.
 */

/** Probed owner indexes per sender (frontends assign 0,1,2,…). */
const OWNER_INDEX_PROBE_COUNT = 8
const CACHE_TTL_MS = 5 * 60_000

export interface LiquityDiscoveredTrove {
  /** decimal uint256 troveId */
  troveId: string
  zombie: boolean
}

export interface LiquityDiscovery {
  /** Discovered troves per branch, index-aligned with `liquityBranchesByChain`. */
  perBranch: LiquityDiscoveredTrove[][]
  at: number
}

const discoveryCache = new Map<string, LiquityDiscovery>()

/**
 * True when trove DISCOVERY on this chain goes through `PositionLens`.
 *
 * Only discovery — the data phase below is unchanged either way, because the lens
 * answers exactly the question the probe multicall answered: which candidate ids
 * are live, and which of them are zombies.
 */
export const usesLiquityLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.LIQUITY) !== undefined

/**
 * Discovery in ONE call instead of `branches x candidates`.
 *
 * This is the single biggest win the lens offers anywhere in this package.
 * Candidate ids are `ownerIndexes x (1 + zappers)` per branch, so Nerite's 8
 * branches cost **1,088 status calls on every read for every account**, whether or
 * not the account has ever touched the protocol — measured at ~4.2 s, the slowest
 * component of an Arbitrum user read, against a book of seven troves.
 *
 * The lens gates each branch on one `TroveNFT.balanceOf` and derives the candidate
 * ids internally (they are pure keccak, so hashing them is cheaper than shipping
 * ~35 KB of calldata), then breaks as soon as it has found as many live troves as
 * `balanceOf` reported.
 *
 * Returns `undefined` when the lens is unavailable or its answer is unusable, and
 * the caller falls back to the probe multicall. It must NOT be read as "no troves":
 * an empty discovery and a failed one are the same shape here, and the difference
 * is a user's whole position.
 */
async function discoverViaLens(
  chainId: string,
  branches: LiquityBranchConfig[],
  zappers: string[],
  account: string,
): Promise<LiquityDiscoveredTrove[][] | undefined> {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.LIQUITY)
  if (!lens) return undefined

  const payload = encodeAbiParameters(
    [
      {
        type: 'tuple[]',
        components: [
          { name: 'troveNFT', type: 'address' },
          { name: 'troveManager', type: 'address' },
        ],
      },
      { type: 'address[]' },
      { type: 'uint8' },
    ],
    [
      branches.map((b) => ({
        troveNFT: b.troveNFT as `0x${string}`,
        troveManager: b.troveManager as `0x${string}`,
      })) as any,
      zappers as `0x${string}`[],
      OWNER_INDEX_PROBE_COUNT,
    ],
  )

  let blobs: any
  try {
    const res = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        {
          address: lens,
          name: 'getUserData',
          params: [[{ family: LENS_FAMILY.LIQUITY, payload }], account],
        },
      ],
      abi: PositionLensAbi as any,
      allowFailure: true,
    })) as any[]
    blobs = res?.[0]
  } catch {
    return undefined
  }
  if (!Array.isArray(blobs) || blobs.length !== 1) return undefined

  let rows
  try {
    // One word per row (the trove id); bit0 of the flags byte is the zombie mark.
    rows = decodeLensRows(blobs[0] as string, 1, 0, 1).rows
  } catch {
    return undefined
  }

  const perBranch: LiquityDiscoveredTrove[][] = branches.map(() => [])
  for (const r of rows) {
    // A branch index past the roster means the payload and the answer disagree —
    // decline the whole discovery rather than dropping the row, because a
    // misaligned branch would attribute a trove to the wrong market.
    if (r.index >= branches.length) return undefined
    perBranch[r.index].push({
      troveId: r.words[0].toString(),
      zombie: (r.flags & 1) !== 0,
    })
  }
  return perBranch
}

const cacheKey = (chainId: string, lender: string, account: string) =>
  `${chainId}:${lender}:${account.toLowerCase()}`

export const getCachedLiquityTroves = (
  chainId: string,
  lender: string,
  account: string,
): LiquityDiscovery | undefined => {
  const hit = discoveryCache.get(cacheKey(chainId, lender, account))
  if (!hit || Date.now() - hit.at > CACHE_TTL_MS) return undefined
  return hit
}

const ADDRESS_ADDRESS_UINT = [
  { type: 'address' },
  { type: 'address' },
  { type: 'uint256' },
] as const
const ADDRESS_UINT = [{ type: 'address' }, { type: 'uint256' }] as const

/** Candidate trove ids for one user: direct + per-zapper salted, as decimal strings. */
export function liquityCandidateTroveIds(
  account: string,
  zappers: string[],
): string[] {
  const user = account as `0x${string}`
  const ids: string[] = []
  for (let i = 0; i < OWNER_INDEX_PROBE_COUNT; i++) {
    ids.push(
      BigInt(
        keccak256(
          encodeAbiParameters(ADDRESS_ADDRESS_UINT as any, [
            user,
            user,
            BigInt(i),
          ]),
        ),
      ).toString(),
    )
    for (const z of zappers) {
      const saltedIndex = BigInt(
        keccak256(encodeAbiParameters(ADDRESS_UINT as any, [user, BigInt(i)])),
      )
      ids.push(
        BigInt(
          keccak256(
            encodeAbiParameters(ADDRESS_ADDRESS_UINT as any, [
              z as `0x${string}`,
              user,
              saltedIndex,
            ]),
          ),
        ).toString(),
      )
    }
  }
  return ids
}

/** Multicall slots one branch consumes (keep in sync with the parser). */
export const liquityBranchCallCount = (
  branch: LiquityBranchConfig,
  troveCount: number,
): number => troveCount * 2 + 4 + (branch.collSurplusPool ? 1 : 0)

/**
 * Async build: runs the discovery status multicall itself, caches the found
 * per-branch trove ids, then returns the data-phase call set executed by the
 * shared sharded multicall (against the merged Liquity ABI from `getAbi`):
 *
 * per branch (config order):
 *   troves × [TroveManager.getLatestTroveData(id), TroveNFT.ownerOf(id)]
 *   + StabilityPool.[getCompoundedBoldDeposit, getDepositorYieldGainWithPending,
 *     getDepositorCollGain, stashedColl](account)
 *   + CollSurplusPool.getCollateral(account)        (when configured)
 */
export const buildLiquityUserCall = async (
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> => {
  const cfg = liquityConfigFor(lender, chainId)
  const branches = liquityBranchesByChain(lender, chainId)
  if (!cfg || branches.length === 0) return []

  const zappers = cfg.zappers ?? []

  // ONE call replaces the whole probe multicall where the lens is deployed. A
  // failure falls through to the probe below rather than reporting no troves.
  let perBranch = await discoverViaLens(chainId, branches, zappers, account)

  if (!perBranch) {
    perBranch = await discoverByProbing(chainId, branches, zappers, account)
    if (!perBranch) return []
  }

  discoveryCache.set(cacheKey(chainId, lender, account), {
    perBranch,
    at: Date.now(),
  })

  return buildDataPhaseCalls(branches, perBranch, account)
}

/**
 * The original discovery: one `getTroveStatus` per (branch, candidate).
 *
 * Kept as the fallback for chains with no lens deployment, and as the emergency
 * revert — removing a chain from the address map restores this path with no other
 * code change. Returns `undefined` when the RPC could not answer, which is NOT the
 * same as an empty result.
 */
async function discoverByProbing(
  chainId: string,
  branches: LiquityBranchConfig[],
  zappers: string[],
  account: string,
): Promise<LiquityDiscoveredTrove[][] | undefined> {
  const candidates = liquityCandidateTroveIds(account, zappers)

  let statuses: any[] = []
  try {
    statuses = (await multicallRetryUniversal({
      chain: chainId,
      calls: branches.flatMap((b) =>
        candidates.map((id) => ({
          address: b.troveManager,
          name: 'getTroveStatus',
          params: [id],
        })),
      ),
      abi: LiquityTroveManagerAbi as any,
      allowFailure: true,
    })) as any[]
  } catch {
    // Discovery unavailable (RPC blip) — report nothing rather than a broken
    // partial layout; the next request retries.
    return undefined
  }

  return branches.map((_, bi) =>
    candidates
      .map((id, ci) => {
        const r = statuses[bi * candidates.length + ci]
        // Failed (allowFailure) slots arrive as '0x'.
        const status =
          typeof r === 'bigint' || typeof r === 'number' ? Number(r) : 0
        // 1 = active, 4 = zombie (redeemed below min debt) — both are live
        // positions; zombies must be adjusted via adjustZombieTrove.
        if (status !== 1 && status !== 4) return undefined
        return { troveId: id, zombie: status === 4 }
      })
      .filter((t): t is LiquityDiscoveredTrove => !!t),
  )
}

/** The data phase, identical for both discovery paths. */
function buildDataPhaseCalls(
  branches: LiquityBranchConfig[],
  perBranch: LiquityDiscoveredTrove[][],
  account: string,
): Call[] {
  const calls: Call[] = []
  branches.forEach((b, bi) => {
    for (const t of perBranch[bi]) {
      calls.push({
        address: b.troveManager,
        name: 'getLatestTroveData',
        params: [t.troveId],
      })
      calls.push({ address: b.troveNFT, name: 'ownerOf', params: [t.troveId] })
    }
    calls.push({
      address: b.stabilityPool,
      name: 'getCompoundedBoldDeposit',
      params: [account],
    })
    calls.push({
      address: b.stabilityPool,
      name: 'getDepositorYieldGainWithPending',
      params: [account],
    })
    calls.push({
      address: b.stabilityPool,
      name: 'getDepositorCollGain',
      params: [account],
    })
    calls.push({
      address: b.stabilityPool,
      name: 'stashedColl',
      params: [account],
    })
    if (b.collSurplusPool) {
      calls.push({
        address: b.collSurplusPool,
        name: 'getCollateral',
        params: [account],
      })
    }
  })

  return calls
}
